import { useTranslation } from "react-i18next";
import { Maximize2, Pause, Play, Square } from "lucide-react";
import { useNavigate } from "react-router";
import {
  phaseMinutes,
  readPomodoro,
  remainingMs,
  startPomodoro,
  stopPomodoro,
} from "../../tracking/pomodoro";
import { readSettings } from "../../app/settings";
import { atLeast } from "../../app/settings";
import { registerViewType, type ViewProps } from "../../registry/viewTypes";
import { useDocSelector, useDoc } from "../../core/useDocSelector";
import { readTask } from "../../core/tasks";
import { fmtElapsed, fmtMinutes } from "../../core/format";
import {
  elapsedMs,
  entriesOf,
  minutesOnDay,
  readCurrent,
  shardIdFor,
  stopTracking,
  readAllCurrent,
  readFocus,
} from "../../tracking/timelog";
import { useCircle } from "../../circle/useCircle";
import { Button } from "../../ui";

export interface TrackingStatusConfig {
  showPomodoro?: boolean;
  showFocus?: boolean;
  showToday?: boolean;
}

/** Kiosk-friendly timer card (spec §9.7 `tracking-status`): current task, elapsed, today's total. */
import { pauseTimer, resumeTimer, pausePom, resumePom } from "../../tracking/linked";
export function TrackingStatusView({ config, ctx }: ViewProps<TrackingStatusConfig>) {
  const { t } = useTranslation();
  const current = useDocSelector(ctx.handle, (doc) => readCurrent(doc));
  const title = useDocSelector(ctx.handle, (doc) => {
    const cur = readCurrent(doc);
    return cur ? (readTask(doc, cur.taskId)?.title ?? "") : "";
  });
  const navigate = useNavigate();
  const pomodoro = useDocSelector(ctx.handle, (doc) => readPomodoro(doc));
  const pomSettings = useDocSelector(ctx.handle, (doc) => readSettings(doc).pomodoro);
  const focus = useDocSelector(ctx.handle, (doc) => readFocus(doc));
  const shard = useDoc(shardIdFor(ctx.workspaceId, new Date(ctx.now)));
  const entries = useDocSelector(shard, (doc) => entriesOf(doc)) ?? [];
  const today = minutesOnDay(entries, new Date(ctx.now), ctx.now);
  const circle = useCircle(ctx.handle);
  const everyone = useDocSelector(ctx.handle, (doc) =>
    readAllCurrent(doc).map((c) => ({ ...c, title: readTask(doc, c.current.taskId)?.title ?? "" })),
  );

  if (ctx.profileKind === "kid") return null; // kids: no time tracking (spec §9.29)
  if (circle.shared && (ctx.profileKind === "viewer" || ctx.readOnly)) {
    // the wall kiosk: one card per person currently tracking
    return (
      <div className="flex h-full flex-col gap-2" data-testid="tracking-status-view">
        {(everyone ?? []).length === 0 && (
          <div className="kss-surface p-5 text-[1.2em] text-neutral-500">{t("trackingView.idle")}</div>
        )}
        {(everyone ?? []).map((c) => (
          <div key={c.current.entryId} className="kss-surface p-4" data-testid="tracking-person">
            <p className="text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
              {circle.nameOf(c.profileId) || t("trackingView.now")}
            </p>
            <p className="truncate text-[1.2em] font-semibold">{c.title || t("task.untitled")}</p>
            <p className="font-mono text-[1.8em] tabular-nums">{fmtElapsed(elapsedMs(c.current, ctx.now))}</p>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="kss-surface flex h-full flex-col justify-between p-5" data-testid="tracking-status-view">
      {current ? (
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
            {t("trackingView.now")}
          </p>
          <button
            type="button"
            className="mt-1 block max-w-full truncate text-left text-[1.4em] font-semibold hover:text-accent"
            onClick={() => ctx.openTask(current.taskId)}
          >
            {title || t("task.untitled")}
          </button>
          <p
            className="mt-2 font-mono text-[2.6em] font-medium leading-none tabular-nums"
            data-testid="tracking-view-elapsed"
          >
            {fmtElapsed(elapsedMs(current, ctx.now))}
          </p>
          <p className="mt-1 text-[12.5px] text-neutral-500">
            {t("trackingView.since", {
              time: new Date(current.startedAt).toLocaleTimeString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
              }),
            })}
          </p>
        </div>
      ) : (
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.06em] text-neutral-500">
            {t("trackingView.now")}
          </p>
          <p className="mt-1 text-[1.2em] text-neutral-500">{t("trackingView.idle")}</p>
        </div>
      )}
      {config.showPomodoro !== false && pomodoro && pomSettings && (
        <div className="mt-3 flex items-center gap-3" data-testid="pomodoro">
          <PomodoroRing
            fraction={
              1 -
              remainingMs(pomodoro, pomSettings, ctx.now) /
                (phaseMinutes(pomodoro.phase, pomSettings) * 60_000)
            }
          />
          <div className="min-w-0 text-[13px]">
            <p className="font-medium">
              {t(`pomodoro.phase.${pomodoro.phase}`)}
              {pomodoro.pausedAt && (
                <span className="font-normal text-neutral-500"> · {t("tracking.paused")}</span>
              )}
            </p>
            <p className="text-neutral-500">
              {t("pomodoro.remaining", { time: fmtElapsed(remainingMs(pomodoro, pomSettings, ctx.now)) })} ·{" "}
              {t("pomodoro.cycle", { n: pomodoro.cycle })}
            </p>
          </div>
        </div>
      )}
      {config.showFocus !== false && focus && (
        <p className="mt-2 text-[12.5px] text-neutral-500" data-testid="focus-note">
          {t("focus.since", {
            time: new Date(focus.startedAt).toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            }),
          })}
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-end justify-between gap-3">
        {config.showToday !== false && (
          <p className="text-[13px] text-neutral-500">
            {t("trackingView.today")}{" "}
            <span className="font-mono text-neutral-900 dark:text-neutral-100">{fmtMinutes(today)}</span>
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2">
          {config.showPomodoro !== false && !ctx.readOnly && pomodoro && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                pomodoro.pausedAt ? void resumePom(ctx.doc, ctx.workspaceId) : void pausePom(ctx.doc)
              }
              data-testid="pomodoro-pause"
            >
              {pomodoro.pausedAt ? t("pomodoro.resume") : t("pomodoro.pause")}
            </Button>
          )}
          {config.showPomodoro !== false && !ctx.readOnly && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => (pomodoro ? stopPomodoro(ctx.doc) : startPomodoro(ctx.doc))}
              data-testid="pomodoro-toggle"
            >
              {pomodoro ? t("pomodoro.stop") : t("pomodoro.start")}
            </Button>
          )}
          {(current || pomodoro) && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={t("bigClock.open")}
              title={t("bigClock.open")}
              className="w-[30px] px-0"
              onClick={() => navigate(current ? `/focus/${current.taskId}?large=1` : "/focus")}
              data-testid="tracking-view-big"
            >
              <Maximize2 size={13} aria-hidden />
            </Button>
          )}
          {current && atLeast(ctx.level, "simple") && (
            <Button
              variant="ghost"
              size="sm"
              aria-label={t("focus.open")}
              title={t("focus.open")}
              className="w-[30px] px-0"
              onClick={() => navigate(`/focus/${current.taskId}`)}
            >
              <Maximize2 size={13} aria-hidden />
            </Button>
          )}
          {current && !ctx.readOnly && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                void (current.pausedAt ? resumeTimer(ctx.doc, ctx.workspaceId) : pauseTimer(ctx.doc))
              }
              data-testid="tracking-view-pause"
            >
              {current.pausedAt ? <Play size={13} aria-hidden /> : <Pause size={13} aria-hidden />}{" "}
              {current.pausedAt ? t("tracking.resume") : t("tracking.pause")}
            </Button>
          )}
          {current && !ctx.readOnly && (
            <Button variant="secondary" size="sm" onClick={() => void stopTracking(ctx.doc)}>
              <Square size={13} aria-hidden /> {t("tracking.stop")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** A ring that fills as the phase passes; the words beside it carry the state (never colour alone). */
function PomodoroRing({ fraction }: { fraction: number }) {
  const r = 16;
  const c = 2 * Math.PI * r;
  const f = Math.min(1, Math.max(0, fraction));
  return (
    <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden>
      <circle cx="20" cy="20" r={r} fill="none" stroke="var(--line-strong)" strokeWidth="4" />
      <circle
        cx="20"
        cy="20"
        r={r}
        fill="none"
        stroke="var(--color-accent)"
        strokeWidth="4"
        strokeDasharray={`${c * f} ${c}`}
        strokeLinecap="round"
        transform="rotate(-90 20 20)"
      />
    </svg>
  );
}

registerViewType<TrackingStatusConfig>({
  id: "tracking-status",
  label: "views.tracking-status.label",
  icon: "timer",
  level: "simple",
  defaultConfig: { showPomodoro: true, showFocus: true, showToday: true },
  // the three options were read but could not be set: Configure said "no options" (UI cleanup U2)
  configSchema: {
    type: "object",
    properties: {
      showPomodoro: { type: "boolean", title: "config.showPomodoro" },
      showFocus: { type: "boolean", title: "config.showFocus" },
      showToday: { type: "boolean", title: "config.showToday" },
    },
  },
  Component: TrackingStatusView,
  minSize: { w: 3, h: 2 },
});
